import { useMemo } from 'react';
import { useTokens } from './tokens';

/**
 * Editor de regra de estoque — filtro "estoque do provider X no(s) item(ns) Y
 * é menor/maior que Z". Componente shared entre Toolbar overrides e PresetBuilder.
 *
 * Shape da rule:
 *   {
 *     provider:  'bling' | 'ml'
 *     depots:    string[]   // ids dos depósitos Bling (vazio = nenhum)
 *     measures:  string[]   // pra ML: ['avail'] | ['unavail'] | ['avail','unavail']
 *                           //          vazio = nenhum (somatório de 0)
 *     op:        'lt' | 'gt'
 *     value:     number
 *   }
 *
 * UI uniforme: 3 seções em vertical
 *   1. Provider — 2 botões lado a lado (Bling | Mercado Livre)
 *   2. Itens — lista de checkboxes com "Selecionar todos" no topo
 *      - Bling: lista de depósitos da org
 *      - Mercado Livre: ["Disponível", "Não disponível"] (escopo atual; futuro
 *        pode incluir InventoryId específicos do ML Full)
 *   3. Condição — op (< ou >) + valor numérico
 *
 * "Selecionar todos" liga = marca todos os items; desliga = desmarca. Estado
 * "todos marcados" é DERIVADO (selected.length === all.length), não um flag
 * separado — evita inconsistência.
 *
 * Props:
 *   rule, onChange, warehouses
 */
const ML_MEASURES = [
    { id: 'avail', label: 'Disponível' },
    { id: 'unavail', label: 'Não disponível' },
];

export function StockRuleEditor({ rule, onChange, warehouses = [], availableProviders }) {
    const T = useTokens();
    const provider = rule?.provider || 'bling';
    const depots = Array.isArray(rule?.depots) ? rule.depots : [];
    // measures (array) substitui a chave antiga `measure` (single). Defaults pra
    // ['avail'] quando ainda não selecionou nada — sem isso a regra começaria
    // não-funcional (sempre 0).
    const measures = Array.isArray(rule?.measures) ? rule.measures : rule?.measure ? [rule.measure] : ['avail'];
    const op = rule?.op || 'lt';
    const value = rule?.value ?? 0;

    const patch = (next) => onChange?.({ ...rule, ...next });

    // Items dinâmicos por provider — mesmo shape pros 2: { id, label }.
    // Bling: depósitos da org. ML: avail/unavail (fixo, pode expandir no futuro).
    const items = useMemo(() => {
        if (provider === 'bling') {
            return (warehouses || []).map((w) => ({ id: String(w.id), label: w.name || String(w.id) }));
        }
        return ML_MEASURES;
    }, [provider, warehouses]);

    const selectedIds = provider === 'bling' ? depots : measures;
    const allIds = items.map((it) => it.id);
    const allSelected = items.length > 0 && allIds.every((id) => selectedIds.includes(id));
    // Sem bloqueio de "desmarcar todos" — user pode querer marcar apenas 1
    // ou 2 dos N depósitos. Fluxo natural: clica "Desmarcar todos" e marca os
    // que quer. Estado vazio é VÁLIDO (filtro não exibe nada, comportamento
    // explícito do user).
    const toggleItem = (id) => {
        const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
        patch(provider === 'bling' ? { depots: next } : { measures: next });
    };
    const toggleAll = () => {
        // Toggle real: se todos marcados → desmarca tudo; senão marca todos.
        const next = allSelected ? [] : [...allIds];
        patch(provider === 'bling' ? { depots: next } : { measures: next });
    };

    return (
        <div
            data-faid="Stock_Rule_Editor_Operacoes"
            style={{
                background: T.bg,
                padding: 10,
                minWidth: 260,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
            }}
        >
            {/* Seção 1: Provider — 2 botões lado a lado. Trocar pra um provider
                já em uso (outra regra) fica desabilitado pra impedir duplicação
                (ex: "Mercado Livre > 10" e "Mercado Livre > 20" simultaneamente).
                Se `availableProviders` não passar, ambos ficam habilitados (fallback). */}
            <div style={{ display: 'flex', gap: 4 }}>
                {[
                    { id: 'bling', label: 'Bling', color: '#288F4E' },
                    { id: 'ml', label: 'Mercado Livre', color: '#ffe600' },
                ].map((p) => {
                    const active = provider === p.id;
                    const allowed = !availableProviders || availableProviders.includes(p.id) || active;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            disabled={!allowed}
                            onClick={() => {
                                if (!allowed) return;
                                // Trocar provider zera a seleção do anterior pra evitar
                                // herdar IDs que não fazem sentido no novo (depots Bling
                                // não existem em 'avail/unavail' do ML e vice-versa).
                                onChange?.({
                                    ...rule,
                                    provider: p.id,
                                    depots: [],
                                    measures: p.id === 'ml' ? ['avail'] : [],
                                });
                            }}
                            title={!allowed ? 'Já existe uma regra desse provider' : undefined}
                            style={{
                                flex: 1,
                                padding: '6px 10px',
                                fontSize: 12,
                                fontWeight: 600,
                                border: `1px solid ${active ? p.color : T.line}`,
                                background: active ? p.color : 'transparent',
                                color: active ? (p.id === 'ml' ? '#1a1a1a' : '#fff') : T.muted,
                                borderRadius: 4,
                                cursor: allowed ? 'pointer' : 'not-allowed',
                                opacity: allowed ? 1 : 0.4,
                                outline: 'none',
                            }}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>

            {/* Seção 2: Lista de itens — mesma estrutura pros 2 providers. */}
            <div>
                <div
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: T.muted,
                        marginBottom: 4,
                    }}
                >
                    {provider === 'bling' ? 'Depósitos' : 'Inventários'} ({selectedIds.length}/{items.length})
                </div>
                <div
                    style={{
                        border: `1px solid ${T.line}`,
                        borderRadius: 4,
                        maxHeight: 180,
                        overflowY: 'auto',
                        background: T.bg,
                    }}
                >
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: items.length > 0 ? 'pointer' : 'default',
                            borderBottom: `1px solid ${T.line}`,
                            background: allSelected ? T.bgSoft : 'transparent',
                            fontWeight: allSelected ? 600 : 500,
                            color: items.length === 0 ? T.muted : T.ink,
                            fontStyle: items.length === 0 ? 'italic' : 'normal',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={allSelected}
                            disabled={items.length === 0}
                            onChange={toggleAll}
                        />
                        Selecionar todos
                    </label>
                    {items.length === 0 && (
                        <div style={{ padding: '8px 10px', fontSize: 11, color: T.muted, fontStyle: 'italic' }}>
                            Nenhum item disponível
                        </div>
                    )}
                    {items.map((it) => {
                        const checked = selectedIds.includes(it.id);
                        return (
                            <label
                                key={it.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 10px',
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    background: checked ? T.bgSoft : 'transparent',
                                    color: T.ink,
                                }}
                            >
                                <input type="checkbox" checked={checked} onChange={() => toggleItem(it.id)} />
                                <span style={{ flex: 1 }}>{it.label}</span>
                                {provider === 'bling' && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            color: T.subtle,
                                            fontFamily: T.mono,
                                        }}
                                    >
                                        {it.id}
                                    </span>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Seção 3: Condição. */}
            <div>
                <div
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: T.muted,
                        marginBottom: 4,
                    }}
                >
                    Condição
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <select
                        value={op}
                        onChange={(e) => patch({ op: e.target.value })}
                        style={{
                            padding: '6px 8px',
                            borderRadius: 4,
                            border: `1px solid ${T.line}`,
                            background: T.bg,
                            color: T.ink,
                            fontSize: 12,
                            cursor: 'pointer',
                            outline: 'none',
                            width: 130,
                        }}
                    >
                        <option value="lt">Menor que (&lt;)</option>
                        <option value="gt">Maior que (&gt;)</option>
                    </select>
                    <input
                        type="number"
                        min={0}
                        value={value}
                        onChange={(e) => patch({ value: e.target.value === '' ? 0 : Number(e.target.value) })}
                        style={{
                            flex: 1,
                            padding: '6px 8px',
                            borderRadius: 4,
                            border: `1px solid ${T.line}`,
                            background: T.bg,
                            color: T.ink,
                            fontSize: 12,
                            fontFamily: T.mono,
                            outline: 'none',
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * Label do chip — ex: "Bling (Principal) < 10" / "Mercado Livre (Disp.+N.Disp.) > 0".
 */
export function FormatStockRuleLabel(rule, warehouses = []) {
    if (!rule) return '—';
    // Texto por extenso + símbolo entre parênteses — usuário pediu pra deixar
    // explícito o operador em vez de mostrar só "< 10" (que requer ler símbolo
    // sem contexto). Ex: "menor que (<) 10" / "maior que (>) 0".
    const opLabel = rule.op === 'gt' ? 'maior que (>)' : 'menor que (<)';
    const value = rule.value ?? 0;
    if (rule.provider === 'bling') {
        const depots = Array.isArray(rule.depots) ? rule.depots : [];
        const allDepotIds = (warehouses || []).map((w) => String(w.id));
        const isAll = depots.length > 0 && allDepotIds.every((id) => depots.includes(id));
        let depotsText;
        if (depots.length === 0) {
            depotsText = ' (nenhum)';
        } else if (isAll) {
            depotsText = ' (todos)';
        } else if (depots.length === 1) {
            const w = (warehouses || []).find((x) => String(x.id) === String(depots[0]));
            depotsText = ` (${w?.name || depots[0]})`;
        } else {
            depotsText = ` (${depots.length} dep.)`;
        }
        return `Bling${depotsText} ${opLabel} ${value}`;
    }
    if (rule.provider === 'ml') {
        const measures = Array.isArray(rule.measures) ? rule.measures : rule.measure ? [rule.measure] : ['avail'];
        const allMeasures = measures.length === ML_MEASURES.length;
        let measuresText;
        if (measures.length === 0) {
            measuresText = ' (nenhum)';
        } else if (allMeasures) {
            measuresText = ' (todos)';
        } else if (measures.length === 1) {
            const m = ML_MEASURES.find((x) => x.id === measures[0]);
            measuresText = ` (${m?.label?.toLowerCase() || measures[0]})`;
        } else {
            measuresText = ` (${measures.join('+')})`;
        }
        return `Mercado Livre${measuresText} ${opLabel} ${value}`;
    }
    return '—';
}

/**
 * Cria uma rule nova com defaults sensatos. Aceita `provider` opcional +
 * `warehouses` (opcional) pra pré-selecionar TODOS os depósitos no Bling.
 *
 * Defaults:
 *   - bling: TODOS depots selecionados (regra: "estoque total Bling < X").
 *            User remove os que não quer; vazio é inválido na UI.
 *   - ml:    'avail' pré-selecionado (caso de uso mais comum).
 *   - op:    < (menor que) — match com regra de reposição típica.
 *   - value: 0
 *
 * Antes começava com `depots: []` (vazio), o que efetivamente fazia o filtro
 * não filtrar nada e era confuso pro usuário. Default = "todos" é mais útil.
 */
export function MakeDefaultStockRule(provider = 'bling', warehouses = []) {
    const allDepotIds = (warehouses || []).map((w) => String(w.id));
    return {
        provider,
        depots: provider === 'bling' ? allDepotIds : [],
        measures: provider === 'ml' ? ['avail'] : [],
        op: 'lt',
        value: 0,
    };
}

/**
 * Helper pro caller (Toolbar / PresetBuilder) impedir duplicação de regra por
 * provider. Devolve true se já existe regra pro provider em `rules`.
 */
export function HasStockRuleFor(provider, rules) {
    return Array.isArray(rules) && rules.some((r) => r?.provider === provider);
}
